import { useRef, useState, useEffect } from 'react';
import { Box, Typography, Chip } from '@mui/material';

const STORY_STEPS = [
  {
    time: 'כל יום, כל עדכון',
    title: 'כל אחד יכול לעדכן — בלי הדרכה, בלי עזרה',
    text: 'רוצה להוסיף הכרזה? לשנות זמן? להכריז על שמחה? עם inorai כל אחד יכול לעשות את זה בדקות — מכל מקום, בכל עת. בלי לחכות לאיש.',
    tag: 'פשוט לכל אחד',
  },
  {
    time: 'ועוד בדרך',
    title: '21 רכיבים היום — ועוד מגיעים',
    text: 'זמני תפילה, שמחות, פרנסים, מזג האוויר, שעון, זמני חגים, מתנדבי חירום — ועוד. כל רכיב חדש שנוסף מגיע לכל בית כנסת מיד. ברגע שגבאי אחד ביקש — כולם נהנים.',
    tag: 'עולם תוכן שגדל',
  },
  {
    time: 'כל קהילה ועולמה',
    title: 'לא תבנית אחת לכולם',
    text: 'שמחות רק בין התפילות? לוח שבת שעובר לבד ביום שישי? פרנס השבוע שמתחלף לפי הלוח העברי? אתה מגדיר מה מוצג ומתי — המערכת דואגת לשאר.',
    tag: 'מותאם לקהילה שלך',
  },
  {
    time: 'ההשקעה שמשתלמת',
    title: 'הצג הוא הפנים של הקהילה שלך',
    text: 'מגוון תבניות עיצוב, גדלים ואפשרויות תצוגה — כדי שבית הכנסת שלך ייראה כמו שהוא ראוי. ומה שתרכוש היום יהיה טוב יותר בשבוע הבא — בלי תשלום נוסף, בלי שתצטרך לעשות דבר.',
    tag: 'עיצוב שמכבד',
  },
];

/* ── Visual 0 — 3-step flow: edit from anywhere ── */
const Visual0 = () => (
  <Box sx={{ width: 230, display: 'flex', flexDirection: 'column', py: 1 }}>
    {[
      { n: '01', text: 'פותח את האפליקציה' },
      { n: '02', text: 'עורך — בלי הדרכה' },
      { n: '03', text: 'הצג מתעדכן מיד' },
    ].map(({ n, text }, i, arr) => (
      <Box key={n}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{
            width: 38, height: 38, borderRadius: '50%', flexShrink: 0,
            border: '1.5px solid rgba(154,114,9,0.42)',
            bgcolor: 'rgba(154,114,9,0.07)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Typography sx={{ fontFamily: 'Secular One, sans-serif', color: '#9a7209', fontSize: '0.7rem' }}>
              {n}
            </Typography>
          </Box>
          <Typography sx={{ fontFamily: 'Heebo, sans-serif', color: '#3d4f61', fontSize: '0.95rem' }}>
            {text}
          </Typography>
        </Box>
        {i < arr.length - 1 && (
          <Box sx={{ width: '1.5px', height: 22, bgcolor: 'rgba(154,114,9,0.18)', ml: '18px', my: 0.25 }} />
        )}
      </Box>
    ))}
  </Box>
);

/* ── Visual 1 — "16+" stat + widget dots grid ── */
const Visual1 = () => (
  <Box sx={{ width: 230, display: 'flex', flexDirection: 'column', gap: 1.5, py: 1 }}>
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
      <Typography sx={{
        fontFamily: 'Frank Ruhl Libre, serif',
        color: '#1e3a5f',
        fontSize: '4.8rem',
        fontWeight: 900,
        lineHeight: 1,
      }}>
        21
      </Typography>
      <Typography sx={{
        fontFamily: 'Frank Ruhl Libre, serif',
        color: '#9a7209',
        fontSize: '2.8rem',
        fontWeight: 900,
        lineHeight: 1,
      }}>
        +
      </Typography>
    </Box>
    <Typography sx={{ fontFamily: 'Heebo, sans-serif', color: '#5a6978', fontSize: '0.88rem' }}>
      רכיבים ייחודיים — ועוד מגיעים
    </Typography>
    {/* Widget dot grid */}
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: '5px', mt: 0.5 }}>
      {Array.from({ length: 21 }).map((_, i) => (
        <Box key={i} sx={{
          width: 14, height: 14,
          borderRadius: '3px',
          bgcolor: `rgba(154,114,9,${0.13 + (i / 20) * 0.6})`,
        }} />
      ))}
    </Box>
  </Box>
);

/* ── Visual 2 — 3 layout cards (customisation) ── */
const LAYOUT_CARDS = [
  { blocks: [55, 40, 70, 35, 50] },
  { blocks: [80, 50, 65, 45, 55, 40], main: true },
  { blocks: [45, 70, 30, 60, 50] },
];

const Visual2 = () => (
  <Box sx={{ width: 230, height: 180, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
    {LAYOUT_CARDS.map(({ blocks, main }, i) => (
      <Box key={i} sx={{
        width:  main ? 72 : 58,
        height: main ? 148 : 118,
        borderRadius: 2,
        border: `${main ? 1.5 : 1}px solid ${main ? 'rgba(154,114,9,0.45)' : 'rgba(30,58,95,0.13)'}`,
        bgcolor: main ? 'rgba(154,114,9,0.04)' : 'rgba(30,58,95,0.02)',
        p: '9px 8px',
        display: 'flex', flexDirection: 'column', gap: '7px',
        boxShadow: main ? '0 6px 22px rgba(154,114,9,0.1)' : 'none',
      }}>
        {blocks.map((pct, j) => (
          <Box key={j} sx={{
            height: 8,
            width: `${pct}%`,
            borderRadius: 0.5,
            bgcolor: main
              ? `rgba(154,114,9,${0.18 + j * 0.05})`
              : `rgba(30,58,95,${0.07 + j * 0.02})`,
          }} />
        ))}
      </Box>
    ))}
  </Box>
);

/* ── Visual 3 — monitor mockup ── */
const Visual3 = () => (
  <Box sx={{ width: 230, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0 }}>
    {/* Screen */}
    <Box sx={{
      width: 172,
      borderRadius: '10px 10px 4px 4px',
      border: '2px solid rgba(30,58,95,0.2)',
      bgcolor: '#1a3252',
      overflow: 'hidden',
      boxShadow: '0 10px 30px rgba(30,58,95,0.16)',
    }}>
      <Box sx={{ p: '14px 14px 12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {/* Title bar */}
        <Box sx={{ height: 7, width: '62%', borderRadius: 1, bgcolor: 'rgba(212,160,23,0.8)', mx: 'auto' }} />
        <Box sx={{ height: 5, width: '42%', borderRadius: 1, bgcolor: 'rgba(255,255,255,0.25)', mx: 'auto' }} />
        {/* Two widget slots */}
        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
          <Box sx={{ flex: 1, height: 30, borderRadius: 1.5, border: '1px solid rgba(212,160,23,0.28)', bgcolor: 'rgba(212,160,23,0.07)' }} />
          <Box sx={{ flex: 1, height: 30, borderRadius: 1.5, border: '1px solid rgba(255,255,255,0.07)', bgcolor: 'rgba(255,255,255,0.03)' }} />
        </Box>
        <Box sx={{ height: 20, borderRadius: 1.5, border: '1px solid rgba(255,255,255,0.06)', bgcolor: 'rgba(255,255,255,0.02)' }} />
      </Box>
    </Box>
    {/* Stand */}
    <Box sx={{ width: '2px', height: 14, bgcolor: 'rgba(30,58,95,0.2)' }} />
    <Box sx={{ width: 52, height: 3, borderRadius: 1, bgcolor: 'rgba(30,58,95,0.14)' }} />
  </Box>
);

/* ── Story step ── */
const VISUALS = [Visual0, Visual1, Visual2, Visual3];

const StoryStep = ({ step, index }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.35 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const isEven = index % 2 === 0;
  const Visual = VISUALS[index];

  return (
    <Box
      ref={ref}
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: isEven ? 'row' : 'row-reverse' },
        gap: { xs: 3, md: 6 },
        alignItems: 'center',
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(32px)',
        transition: 'opacity 0.7s ease, transform 0.7s ease',
        py: { xs: 4, md: 5 },
        borderBottom: '1px solid rgba(30,58,95,0.07)',
        '&:last-child': { borderBottom: 'none' },
      }}
    >
      {/* Text */}
      <Box sx={{ flex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
          <Typography sx={{
            fontFamily: 'Rubik, sans-serif',
            color: '#9a7209', fontSize: '0.82rem',
            fontWeight: 600, direction: 'rtl',
          }}>
            {step.time}
          </Typography>
          <Chip label={step.tag} size="small" sx={{
            bgcolor: 'rgba(30,58,95,0.07)', color: '#1e3a5f',
            fontFamily: 'Heebo, sans-serif', fontSize: '0.68rem', height: 22,
          }} />
        </Box>

        <Typography sx={{
          fontFamily: 'Secular One, sans-serif',
          color: '#1e3a5f', fontSize: '1.3rem', mb: 1.5,
        }}>
          {step.title}
        </Typography>

        <Typography sx={{
          fontFamily: 'Heebo, sans-serif',
          color: '#5a6978', fontSize: '1rem',
          lineHeight: 1.9, maxWidth: '420px',
        }}>
          {step.text}
        </Typography>
      </Box>

      {/* Visual */}
      <Box sx={{ flexShrink: 0 }}>
        <Visual />
      </Box>
    </Box>
  );
};

/* ── Section ── */
const ScrollytellingSection = () => (
  <Box id="live-demo" sx={{
    bgcolor: '#fcfbfa',
    py: { xs: 10, md: 14 },
    px: { xs: 3, md: 6 },
    borderTop: '1px solid rgba(30,58,95,0.06)',
  }}>
    <Box sx={{ maxWidth: '900px', mx: 'auto' }}>
      <Box sx={{ textAlign: 'center', mb: 8 }}>
        <Typography sx={{
          fontFamily: 'Secular One, sans-serif', color: '#9a7209',
          fontSize: '0.85rem', letterSpacing: '0.12em',
          textTransform: 'uppercase', mb: 2,
        }}>
          למה גבאים בוחרים ב-inorai
        </Typography>
        <Typography component="h2" dir="rtl" sx={{
          fontFamily: 'Frank Ruhl Libre, serif', color: '#1e3a5f',
          fontSize: { xs: '1.9rem', md: '2.5rem' },
          fontWeight: 900, letterSpacing: '-0.02em', mb: 2,
          direction: 'rtl',
        }}>
          הגבאי שלך <Box component="span" className="lp-gold-text">ראוי</Box> לצג שעובד בשבילו
        </Typography>
        <Typography sx={{
          color: '#5a6978', fontFamily: 'Heebo, sans-serif',
          fontSize: '1rem', maxWidth: '440px', mx: 'auto', lineHeight: 1.8,
        }}>
          פשוט, גמיש, יפה — ומתרחב כל הזמן.
        </Typography>
      </Box>

      <Box>
        {STORY_STEPS.map((step, i) => (
          <StoryStep key={i} step={step} index={i} />
        ))}
      </Box>
    </Box>
  </Box>
);

export default ScrollytellingSection;
